import "styles/core.scss";

import config from "config";
import createBrowserHistory from "history/lib/createBrowserHistory";
import jwt_decode from "jwt-decode";
import Keycloak from "keycloak-js";
import { get, includes, isEmpty } from "lodash";
import React from "react";
import ReactDOM from "react-dom";
import { useRouterHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import userService from "./services/user-service";

import AppContainer from "./containers/AppContainer";
import createStore from "./store/createStore";

// ========================================================
// Browser History Setup
// ========================================================
const browserHistory = useRouterHistory(createBrowserHistory)({
  basename: __BASENAME__,
});
const MESSAGE_LOAD_KEYCLOAK_TOKENS = "LOAD_KEYCLOAK_TOKENS";

// ========================================================
// Store and History Instantiation
// ========================================================
// Create redux store and sync with react-router-redux. We have installed the
// react-router-redux reducer under the routerKey "router" in src/routes/index.js,
// so we need to provide a custom `selectLocationState` to inform
// react-router-redux of its location.
const initialState = window.___INITIAL_STATE__;
const store = createStore(initialState, browserHistory);
export const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: (state) => state.router,
});

function loadKeycloakTokens(
  refreshToken,
  authenticationToken,
  posUserToken = undefined
) {
  // need to override existing token for ginesys scenario
  if (posUserToken !== undefined) {
    sessionStorage.setItem("refreshToken", refreshToken);
    sessionStorage.setItem("authentication", authenticationToken);
    sessionStorage.setItem("posUserToken", posUserToken);
  }
  if (isEmpty(sessionStorage.getItem("refreshToken"))) {
    sessionStorage.setItem("refreshToken", refreshToken);
    sessionStorage.setItem("authentication", authenticationToken);
    window.location.reload();
  }
  // need to override existing token for ginesys scenario
  if (posUserToken !== undefined) {
    sessionStorage.setItem("refreshToken", refreshToken);
    sessionStorage.setItem("authentication", authenticationToken);
    sessionStorage.setItem("posUserToken", posUserToken);
  }
}

window["loadKeycloakTokens"] = loadKeycloakTokens;

// ========================================================
// Developer Tools Setup
// ========================================================
if (__DEBUG__) {
  if (window.devToolsExtension) {
    window.devToolsExtension.open();
  }
}

// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById("root");

const conditionRender = (isProd) => {
  let refreshToken = sessionStorage.refreshToken;
  let isLegacy = sessionStorage.isLegacy === "true";
  let authenticationToken = sessionStorage.authentication;

  if (
    includes(window.location.href, "legacyModeOn") ||
    includes(window.location.href, "auto-login-redirect")
  ) {
    sessionStorage.setItem("isLegacy", "true");
    isLegacy = true;
  }
  if (includes(window.location.href, "legacyModeOff")) {
    sessionStorage.setItem("isLegacy", "false");
    isLegacy = true;
  }
  let routerKey = isProd ? undefined : Math.random();

  if (
    isLegacy ||
    (includes(window.location.href, "loadTokenFromContainer") &&
      (isEmpty(refreshToken) || isEmpty(authenticationToken)))
  ) {
    window.ReactNativeWebView &&
      window.ReactNativeWebView.postMessage(MESSAGE_LOAD_KEYCLOAK_TOKENS);
    console.log("wait flow");
    const routes = require("./routes/index").default(store);
    ReactDOM.render(
      <AppContainer
        store={store}
        history={history}
        routes={routes}
        routerKey={routerKey}
      />,
      MOUNT_NODE
    );
  } else if (includes(window.location.href, "loadTokenFromUrl")) {
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);
      console.log("urlSearchParams", urlSearchParams);
      // const params = Object.fromEntries(urlSearchParams.entries());
      let decoded = urlSearchParams.get("token");
      return userService.getPosToken(decoded).then((response) => {
        let savedToken = get(response, "token");
        let decoded = jwt_decode(savedToken);
        loadKeycloakTokens(
          get(decoded, "refreshToken"),
          get(decoded, "token"),
          savedToken
        );
        console.log("wait flow for setting token");
        const routes = require("./routes/index").default(store);
        ReactDOM.render(
          <AppContainer
            store={store}
            history={history}
            routes={routes}
            routerKey={routerKey}
          />,
          MOUNT_NODE
        );
      });
    } catch (error) {
      console.log("error", error);
      // window.location.href = 'https://www.google.com/search?q=' + error
    }
  } else {
    const keycloak = Keycloak(config.keycloackConfig);
    keycloak
      .init({
        onLoad: "login-required",
        promiseType: "native",
        checkLoginIframe: false,
        token: authenticationToken,
        refreshToken: refreshToken,
      })
      .then((authenticated, error) => {
        if (!authenticated) {
          sessionStorage.removeItem("authentication");
          sessionStorage.removeItem("refreshToken");
          window.location.reload();
        } else {
          sessionStorage.setItem("authentication", keycloak.token);
          sessionStorage.setItem("refreshToken", keycloak.refreshToken);
          // login({})
        }
        const routes = require("./routes/index").default(store, keycloak);
        ReactDOM.render(
          <AppContainer
            store={store}
            history={history}
            routes={routes}
            keycloak={keycloak}
            routerKey={routerKey}
          />,
          MOUNT_NODE
        );
      })
      .catch((error) => {
        console.error("Authenticated Failed", error);
      });
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(80400)
        .then(() => {
          sessionStorage.setItem("authentication", keycloak.token);
          sessionStorage.setItem("refreshToken", keycloak.refreshToken);
        })
        .catch((error) => {
          console.error("Authenticated Failed", error);
        });
    };
  }
};

let render = () => {
  conditionRender(true);
};

// Enable HMR and catch runtime errors in RedBox
// This code is excluded from production bundle
if (__DEV__ && module.hot) {
  const renderError = (error) => {
    const RedBox = require("redbox-react").default;
    ReactDOM.render(<RedBox error={error} />, MOUNT_NODE);
  };
  render = () => {
    try {
      conditionRender(false);
    } catch (error) {
      renderError(error);
    }
  };
  module.hot.accept(["./routes/index"], () => render());
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        console.log("SW registered: ", registration);
      })
      .catch((registrationError) => {
        console.log("SW registration failed: ", registrationError);
      });
  });
}
// console.log = () => {};
// console.warn = () => {};

// ========================================================
// Go!
// ========================================================
render();



// WEBPACK FOOTER //
// ./src/main.js